/**
 * Filters Modal DOM Component
 *
 * Owns the Native Interactive Site Map's Filters modal shell (H1, close button, empty body
 * placeholder, CTA footer) — as a static markup string, same as before. The body's actual filter
 * fields are no longer part of that string: they're the shared, stateful `@components/*Filter`
 * components (moved out of this feature so other features/pages can reuse them — see
 * filterFields.js), mounted as live DOM into `[data-filters-modal-body]` after this markup is
 * inserted (filtersModal.plugin.js#openFiltersModal).
 *
 * @module nativeInteractiveSiteMap/components/filtersModal.dom
 */

export const FILTERS_MODAL_ID = 'native-interactive-site-map-filters-modal';

/**
 * Creates (or returns the existing) persistent container `createModal()` targets. Appended to
 * `document.body`, mirroring `layout/components/overlay.js`'s self-contained element creation —
 * its `position: fixed` + high z-index (see SCSS) mean DOM placement doesn't matter for layout.
 * Idempotent: safe to call every time `setupNativeInteractiveSiteMap()` runs.
 */
export function ensureFiltersModalContainer() {
  const existingContainer = document.getElementById(FILTERS_MODAL_ID);
  if (existingContainer) {
    return existingContainer;
  }

  const container = document.createElement('div');
  container.id = FILTERS_MODAL_ID;
  container.className = 'nhs-c-native-interactive-site-map__filters-modal';
  container.setAttribute('aria-label', 'Filters');
  container.setAttribute('role', 'dialog');
  container.setAttribute('aria-modal', 'true');
  document.body.appendChild(container);

  return container;
}

export function createFiltersModalContent() {
  return `
    <div class="nhs-c-native-interactive-site-map__filters-modal-content">
      <div class="nhs-c-native-interactive-site-map__filters-modal-header">
        <h1 class="nhs-c-native-interactive-site-map__filters-modal-title" data-qa="native_ism_filters_title">Filters</h1>
        <button type="button"
                class="nhs-n1-c-button__modal-close nhs-n1-c-button__modal-close--transparent"
                aria-label="Close filters"
                data-close-button
                data-qa="native_ism_filters_close_button">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
      <div class="nhs-c-native-interactive-site-map__filters-modal-body" data-filters-modal-body></div>
      <div class="nhs-c-native-interactive-site-map__filters-modal-footer">
        <button type="button"
                class="nhs-c-button nhs-c-button--medium-40 nhs-n1-c-button--tertiary nhs-c-native-interactive-site-map__filters-modal-cta nhs-u-flex--1"
                disabled
                data-clear-all-button
                data-qa="native_ism_filters_clear_all">
          Clear all filters
        </button>
        <button type="button"
                class="nhs-c-button nhs-c-button--medium-40 nhs-n1-c-button--secondary-dark nhs-c-native-interactive-site-map__filters-modal-cta nhs-c-native-interactive-site-map__filters-modal-cta--show-results nhs-u-flex--1"
                data-show-results-button
                data-qa="native_ism_filters_show_results">
          Show results
        </button>
      </div>
    </div>
  `;
}

/**
 * Binds the modal's own close (X) button. Returns an unbind function.
 * Closing here must never apply or reset filters (NHS20-8047 AC) — it only calls `onClose`.
 */
export function bindFiltersModalClose(modalElement, onClose) {
  const closeButton = modalElement?.querySelector('[data-close-button]');
  if (!closeButton) {
    return null;
  }

  closeButton.addEventListener('click', onClose);
  return () => closeButton.removeEventListener('click', onClose);
}

/**
 * Binds the footer's Clear all filters / Show XY Results buttons. Returns an unbind function.
 */
export function bindFiltersModalFooter(modalElement, { onClearAll, onShowResults }) {
  const clearAllButton = modalElement?.querySelector('[data-clear-all-button]');
  const showResultsButton = modalElement?.querySelector('[data-show-results-button]');

  clearAllButton?.addEventListener('click', onClearAll);
  showResultsButton?.addEventListener('click', onShowResults);

  return () => {
    clearAllButton?.removeEventListener('click', onClearAll);
    showResultsButton?.removeEventListener('click', onShowResults);
  };
}

/**
 * Enables/disables the "Clear all filters" button (NHS20-8049). The markup starts `disabled`
 * (the button's default state); the native `[disabled]`/`:hover` styling already defined for
 * `.nhs-n1-c-button--tertiary` (`_tools.pseudo-classes.scss#nhs-N1-tertiary-pseudo-classes`) gives
 * the greyed-out/enabled/hover looks the design calls for, so toggling the attribute is the only
 * thing this needs to do.
 */
export function setClearAllButtonDisabled(modalElement, isDisabled) {
  const clearAllButton = modalElement?.querySelector('[data-clear-all-button]');
  if (clearAllButton) {
    clearAllButton.disabled = isDisabled;
  }
}

/**
 * Writes the "Show XY Results" label for the given count — the count of results the
 * pending filters would produce, so it's kept live by the plugin as fields change.
 */
export function setShowResultsCount(modalElement, count) {
  const showResultsButton = modalElement?.querySelector('[data-show-results-button]');
  if (showResultsButton) {
    showResultsButton.textContent = `Show ${count} ${count === 1 ? 'result' : 'results'}`;
  }
}